import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'

import {
  formatFairgenSentence,
  formatFairgenTierLabel,
  formatPBestBullets,
  isStudyDocDecisionMastheadVisible,
  normalizeFairgenConfidence,
  normalizeStudyDocDecisionConfidence,
  StudyDocDecisionConfidence,
  StudyDocFairgenConfidence,
} from './formatStudyDocDecisionMasthead'

export type { StudyDocDecisionConfidence, StudyDocFairgenConfidence }
export { isStudyDocDecisionMastheadVisible }

interface StudyDocDecisionMastheadProps {
  decisionConfidence?: StudyDocDecisionConfidence | Record<string, unknown> | null
  fairgenConfidence?: StudyDocFairgenConfidence | Record<string, unknown> | null
  recommendation?: string
  isHidden?: boolean
  className?: string
}

export function StudyDocDecisionMasthead(props: StudyDocDecisionMastheadProps) {
  const {
    decisionConfidence,
    fairgenConfidence,
    recommendation,
    isHidden = false,
    className,
  } = props

  const normalized = normalizeStudyDocDecisionConfidence(decisionConfidence)

  if (!normalized || isHidden) {
    return null
  }

  const authoredRecommendation = (recommendation ?? normalized.recommendation ?? '').trim()
  const fairgen =
    normalizeFairgenConfidence(fairgenConfidence) ?? normalized.fairgenConfidence
  const bullets = formatPBestBullets(normalized)

  const recommendationBlock = authoredRecommendation ? (
    <Text
      as="p"
      size="lg"
      weight="medium"
      className="rounded-lg bg-card-inset px-4 py-3 normal-case tracking-normal"
      data-testid="study-doc-decision-recommendation"
    >
      {authoredRecommendation}
    </Text>
  ) : null

  const fairgenBlock = fairgen ? (
    <div
      className="space-y-1 normal-case tracking-normal"
      data-testid="study-doc-decision-fairgen"
    >
      <Text
        as="p"
        size="sm"
        weight="medium"
        className="normal-case tracking-normal"
        data-testid="study-doc-decision-fairgen-tier"
      >
        {formatFairgenTierLabel(fairgen.tier)}
      </Text>
      <Text
        as="p"
        size="sm"
        color="muted"
        className="normal-case tracking-normal"
        data-testid="study-doc-decision-fairgen-sentence"
      >
        {formatFairgenSentence(fairgen)}
      </Text>
    </div>
  ) : null

  const pBestBlock =
    bullets.length > 0 ? (
      <ul
        className="list-disc space-y-1 pl-5 normal-case tracking-normal"
        data-testid="study-doc-decision-pbest"
      >
        {bullets.map((line, index) => (
          <li key={`${index}-${line}`} data-testid={`study-doc-decision-pbest-${index}`}>
            <Text as="span" size="sm" className="normal-case tracking-normal">
              {line}
            </Text>
          </li>
        ))}
      </ul>
    ) : null

  return (
    <div
      className={cn('space-y-3 normal-case tracking-normal', className)}
      data-testid="study-doc-decision-masthead"
    >
      {recommendationBlock}
      {fairgenBlock}
      {pBestBlock}
    </div>
  )
}
